import React from 'react';
import type { TransactionsFilterState } from './transactionsTypes';
import type { UseSalesTransactionsQueryParams } from '@/api/queries/sales';
import { SalesQueryKeys, useSalesTransactionsQuery } from '@/api';
import { useQueryClient } from '@tanstack/react-query';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { SalesTransaction } from '@/api/models';
import { useSales } from '@/pages/sales/SalesProvider';
import {
    SEARCH_DEBOUNCE_MS,
    TRANSACTIONS_PAGE_SIZE,
    type TransactionTypeFilter,
} from './transactionsMeta';

/**
 * Shape returned by the hook. Mirrors the existing widget contracts
 * (`data` / `error` / `isLoading` / `onRetry`) with the extras the
 * transactions table needs:
 *
 * - `items` is the flat list across every fetched page so the
 *   DataGrid sees one growing array.
 * - `totalCount` is the server's authoritative count, used for the
 *   header subtitle.
 * - `hasMore` / `isFetchingMore` drive the DataGrid's load-more
 *   sentinel; `onLoadMore` fetches the next page.
 * - `search` / `setSearch` own the seller-search input state.
 * - `transactionType` / `setTransactionType` own the payment-family
 *   filter (Segment on the View). `'All'` is the UI sentinel for
 *   "no filter".
 */
export type UseTransactionsResult = TransactionsFilterState & {
    error: ErrorResponse | null;
    hasMore: boolean;
    isFetchingMore: boolean;
    isLoading: boolean;
    items: SalesTransaction[];
    onLoadMore: () => void;
    onRetry: () => void;
    totalCount: number;
};

/**
 * Drives the Transactions widget. Owns the seller-search box and
 * payment-type filter states; the date window comes from the
 * page-level `SalesProvider` so changing the period filter on the
 * page re-keys the query and refetches automatically.
 */
export const useTransactions = (): UseTransactionsResult => {
    const { dateRange } = useSales();
    const queryClient = useQueryClient();
    const [discountedOnly, updateDiscountedOnly] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');
    const [transactionType, setTransactionType] =
        React.useState<TransactionTypeFilter>('All');

    // Debounce the search input so a fast typist doesn't fire a query
    // per keystroke.
    React.useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedSearch(search);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search]);

    const queryParams: UseSalesTransactionsQueryParams = {
        ...(discountedOnly ? { discounted: true } : {}),
        from: dateRange.start,
        pageSize: TRANSACTIONS_PAGE_SIZE,
        sellerName: debouncedSearch.trim() || undefined,
        to: dateRange.end,
        // `'All'` is the UI-only sentinel — omit the param to mean
        // "no filter" on the wire.
        transactionType:
            transactionType === 'All' ? undefined : transactionType,
    };

    const setDiscountedOnly = (value: boolean) => {
        if (value === discountedOnly) return;
        // A previously visited filter may have several cached pages. Start
        // at page one when toggling back, while preserving type and search.
        queryClient.removeQueries({
            queryKey: SalesQueryKeys.transactions({
                ...queryParams,
                discounted: value ? true : undefined,
            }),
            exact: true,
            type: 'inactive',
        });
        updateDiscountedOnly(value);
    };

    const query = useSalesTransactionsQuery(queryParams);
    const { refetch, fetchNextPage } = query;

    const items = React.useMemo<SalesTransaction[]>(() => {
        if (!query.data) return [];
        return query.data.pages.flatMap((envelope) =>
            envelope.success ? envelope.response.items : [],
        );
    }, [query.data]);

    const error = React.useMemo<ErrorResponse | null>(() => {
        if (!query.data) return null;
        // Report only the latest page's error so a transient failure on
        // the next-page load doesn't blow away the rows already shown.
        const last = query.data.pages[query.data.pages.length - 1];
        if (!last || last.success) return null;
        return last.error ?? null;
    }, [query.data]);

    const totalCount = React.useMemo(() => {
        if (!query.data) return 0;
        // Walk back to the last *successful* page so a transient
        // failure on the latest load doesn't flip the subtitle to "0".
        for (let i = query.data.pages.length - 1; i >= 0; i -= 1) {
            const envelope = query.data.pages[i];
            if (envelope?.success) {
                return envelope.response.totalCount;
            }
        }
        return 0;
    }, [query.data]);

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    const onLoadMore = React.useCallback(() => {
        void fetchNextPage();
    }, [fetchNextPage]);

    return {
        discountedOnly,
        setDiscountedOnly,
        error,
        hasMore: query.hasNextPage ?? false,
        isFetchingMore: query.isFetchingNextPage,
        isLoading: query.isPending,
        items,
        onLoadMore,
        onRetry,
        search,
        setSearch,
        setTransactionType,
        totalCount,
        transactionType,
    };
};
